import React from 'react';
import * as T from '../time.js';
import { api } from '../api.js';
import { Empty, Icon, Lbl, Loading, Modal, PH, Panel } from './components.jsx';
import { Button, Field, Pill, SegmentedControl } from './ds/fgcp-ds.jsx';

// booking-detail.jsx — 예약 상세·내 예약·승인 대기(계획 문서 5.2 2-3b). 할 수 있는 일(취소·승인·반려)과 사유 필수 여부는 서버가 준 actions로만
// 정한다 — 화면은 누가 담당자인지·관리자인지 판단하지 않는다. 반려·취소 사유는 서버가 당사자에게만 내려준다.
// 반복 예약(2-3d): 회차의 상세에서 묶음 동작(이 회차부터 모두 취소·반복 전체 바꾸기·반복 전체 승인/반려 — actions.series)과 회차 동작(이 회차 취소·
// 이날만 바꾸기)을 함께 보인다. 승인 대기 목록은 묶음마다 한 줄(서버가 묶어서 준다).

const { useState: uS, useEffect: uE, useCallback: uCb } = React;
const WD = ['월', '화', '수', '목', '금', '토', '일'];
export const BOOKING_STATUS = { reserved: ['확정', 'ok'], pending_approval: ['승인 대기', 'warn'], rejected: ['반려', 'crit'], cancelled: ['취소', 'neutral'] };
export function BookingStatus({ s }) {
  const [label, tone] = BOOKING_STATUS[s] ?? [s, 'neutral'];
  return <Pill tone={tone} dot>{label}</Pill>;
}
const dt = iso => T.format(iso, { month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
/** 시작–끝 — 같은 날(앱 시간대)이면 끝은 시각만 */
export function periodText(b) {
  const same = T.ymd(b.starts_at) === T.ymd(Date.parse(b.ends_at) - 1);
  return same ? `${dt(b.starts_at)} – ${T.hm(b.ends_at)}` : `${dt(b.starts_at)} – ${dt(b.ends_at)}`;
}

/** 반복 규칙 — '매주 월·수 10:00–11:00 · 10/6 ~ 12/29'(서버의 묶음 정보 그대로) */
const md = ymd => { const [, m, d] = ymd.split('-').map(Number); return `${m}/${d}`; };
export const seriesText = s =>
  `${s.freq === 'daily' ? '매일' : '매주 ' + (s.weekdays ?? []).map(d => WD[d - 1]).join('·')} ${s.startTime}–${s.endTime} · ${md(s.startDate)} ~ ${md(s.untilDate)}`;
/** 반복 예약의 회차(묶음에 남은 것)인가 — 떼어 낸 회차는 단독 예약처럼 */
const inSeriesOf = b => Boolean(b?.series && !b.series.detached);

/** 사유를 받는 동작 — 화면 글과 부를 곳 */
const REASON_MODES = {
  cancel: { title: '취소하기', aria: '취소 사유' },
  cancelFrom: { title: '이 회차부터 모두 취소하기', aria: '취소 사유' },
  reject: { title: '반려하기', aria: '반려 사유' },
  rejectSeries: { title: '반복 전체 반려하기', aria: '반려 사유' }
};

/** 예약 상세 창 — 알림 링크(#/booking/<id>)·시간표 블록·목록 행에서 연다 */
export function BookingDetail({ id, onClose, onChanged, onToast, onEdit, onEditSeries }) {
  const [b, setB] = uS(null);
  const [mode, setMode] = uS(null);         // REASON_MODES의 키 — 사유 입력
  const [reason, setReason] = uS('');
  const [busy, setBusy] = uS(false);
  // 닫기·알림 함수는 부르는 쪽이 매번 새로 만들 수 있다 — 다시 불러오기는 id가 바뀔 때만
  const outer = React.useRef({ onToast, onClose });
  outer.current = { onToast, onClose };
  const load = uCb(() => api.get('/api/v1/bookings/' + id).then(r => setB(r.booking))
    .catch(e => { outer.current.onToast(e.message); outer.current.onClose(); }), [id]);
  uE(() => { void load(); }, [load]);
  /** 처리 — done은 알림 글(서버 응답으로 만드는 함수일 수도) */
  const act = (url, body, done) => {
    setBusy(true);
    api.post(url, body)
      .then(r => { onToast(typeof done === 'function' ? done(r) : done); setMode(null); setReason(''); onChanged?.(); return load(); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  const a = b?.actions ?? {}, sa = a.series ?? {};
  const inSeries = inSeriesOf(b);
  const one = `/api/v1/bookings/${id}`, all = b?.series ? `/api/v1/bookings/series/${b.series.id}` : '';
  const needReason = (mode === 'cancel' && a.cancelReasonRequired) || (mode === 'cancelFrom' && sa.cancelFromReasonRequired);
  const why = () => reason.trim() || null;
  const run = {
    cancel: () => act(`${one}/cancel`, { reason: why() }, inSeries ? '이 회차를 취소했습니다' : '예약을 취소했습니다'),
    cancelFrom: () => act(`${all}/cancel`, { fromBookingId: id, reason: why() }, r => `이 회차부터 ${r.cancelled}회를 취소했습니다`),
    reject: () => act(`${one}/reject`, { reason: why() }, '예약을 반려했습니다'),
    rejectSeries: () => act(`${all}/reject`, { reason: why() }, r => `반복 예약 ${r.count}회를 반려했습니다`)
  };
  const fieldLabel = mode === 'reject' || mode === 'rejectSeries' ? '반려 사유 (선택 — 예약자에게 보입니다)'
    : needReason ? '취소 사유 (필수 — 예약자에게 보입니다)' : '취소 사유 (선택)';
  return (
    <Modal width={560} title="예약 상세" icon="calendar" onClose={onClose}
      footer={b && <React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        {mode ? <React.Fragment>
          <Button variant="secondary" size="sm" onClick={() => { setMode(null); setReason(''); }}>돌아가기</Button>
          <Button variant="danger" size="sm" disabled={busy || (needReason && !reason.trim())} onClick={run[mode]}>{REASON_MODES[mode].title}</Button>
        </React.Fragment> : <React.Fragment>
          {a.cancel && <Button variant="danger" size="sm" onClick={() => setMode('cancel')}>{inSeries ? '이 회차 취소' : '예약 취소'}</Button>}
          {sa.cancelFrom && <Button variant="danger" size="sm" onClick={() => setMode('cancelFrom')}>이 회차부터 모두 취소</Button>}
          {a.change && onEdit && <Button variant="secondary" size="sm" onClick={() => onEdit(b)}>{inSeries ? '이날만 바꾸기' : '바꾸기'}</Button>}
          {sa.change && onEditSeries && <Button variant="secondary" size="sm" onClick={() => onEditSeries(b)}>반복 전체 바꾸기</Button>}
          {a.reject && <Button variant="secondary" size="sm" onClick={() => setMode('reject')}>반려</Button>}
          {sa.reject && <Button variant="secondary" size="sm" onClick={() => setMode('rejectSeries')}>반복 전체 반려</Button>}
          {a.approve && <Button variant="primary" size="sm" disabled={busy} onClick={() => act(`${one}/approve`, {}, '예약을 승인했습니다 — 참석자에게 초대가 갑니다')}>승인</Button>}
          {sa.approve && <Button variant="primary" size="sm" disabled={busy}
            onClick={() => act(`${all}/approve`, {}, r => `반복 예약 ${r.count}회를 승인했습니다 — 참석자에게 초대가 갑니다`)}>반복 전체 승인</Button>}
        </React.Fragment>}
      </React.Fragment>}>
      {!b ? <Loading title="불러오는 중" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }} data-booking-detail={b.id} data-status={b.status}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookingStatus s={b.status} />
            <b style={{ fontSize: 15 }}>{b.title}</b>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr', rowGap: 6, columnGap: 10 }}>
            <Lbl>설비</Lbl><span>{b.facility_type} · {b.facility_name}{b.location ? ` · ${b.location}` : ''}</span>
            <Lbl>시간</Lbl><span className="mono">{periodText(b)}</span>
            <Lbl>예약자</Lbl><span>{b.requester_name}{b.requester_department ? ` · ${b.requester_department}` : ''}</span>
            <Lbl>참석자</Lbl><span>{b.attendees.length ? b.attendees.map(x => x.name).join(', ') : '없음'}</span>
            {b.series && <React.Fragment><Lbl>반복</Lbl><span data-series-info={b.series.id} data-detached={b.series.detached}>{b.series.detached
              ? `반복 예약에서 따로 바꾼 회차 — 원래 ${dt(b.series.originalStart)}`
              : `${seriesText(b.series)} · ${b.series.index}/${b.series.count}번째 · 남은 ${b.series.remaining}회`}</span></React.Fragment>}
            {b.approved_by_name && <React.Fragment><Lbl>승인</Lbl><span>{b.approved_by_name} · {dt(b.approved_at)}</span></React.Fragment>}
            {b.rejected_by_name && <React.Fragment><Lbl>반려</Lbl><span>{b.rejected_by_name} · {dt(b.rejected_at)}{b.reject_reason ? ` — ${b.reject_reason}` : ''}</span></React.Fragment>}
            {b.cancelled_at && <React.Fragment><Lbl>취소</Lbl><span>{b.cancelled_by_name ?? ''}{b.cancelled_by_name ? ' · ' : ''}{dt(b.cancelled_at)}{b.cancel_reason ? ` — ${b.cancel_reason}` : ''}</span></React.Fragment>}
          </div>
          {b.status === 'pending_approval' && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{inSeries
            ? '담당자가 반복 전체를 승인하면 모든 회차가 확정되고 참석자에게 초대(알림·Outlook 반복 일정)가 갑니다. 그때까지 이 시간들은 잡혀 있습니다.'
            : '담당자가 승인하면 확정되고 참석자에게 초대(알림·Outlook)가 갑니다. 그때까지 이 시간은 잡혀 있습니다.'}</div>}
          {mode === 'cancelFrom' && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>이 회차와 그 뒤의 끝나지 않은 회차를 모두 취소합니다(따로 바꾼 회차는 그대로).</div>}
          {mode && (
            <Field label={fieldLabel} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-label={REASON_MODES[mode].aria} />
          )}
        </div>
      )}
    </Modal>
  );
}

/** 목록 한 줄 — 상태·제목·설비·시간·예약자(반복 예약의 회차면 반복 표시) */
function Row({ b, onOpen, right }) {
  return (
    <div className="rowlist" data-booking-row={b.id} data-status={b.status} data-series={inSeriesOf(b) || undefined} style={{ cursor: 'pointer' }} onClick={() => onOpen(b.id)}>
      <BookingStatus s={b.status} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {inSeriesOf(b) && <span title="반복 예약" style={{ display: 'inline-flex', flex: 'none' }}><Icon name="refresh-cw" size={11} color="var(--ink-3)" /></span>}{b.title}</span>
        <span className="mono" style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)' }}>{b.facility_name} · {periodText(b)} · {b.requester_name}</span>
      </span>
      {right}
    </div>
  );
}

/** 내 예약 — 예약한 것·참석하는 것 × 다가오는·지난/취소, 쪽 나누기 */
export function MyBookings({ onOpen, onToast, rev }) {
  const [role, setRole] = uS('requested');
  const [period, setPeriod] = uS('upcoming');
  const [page, setPage] = uS(1);
  const [data, setData] = uS(null);
  uE(() => {
    let current = true;
    api.get(`/api/v1/bookings/mine?role=${role}&period=${period}&page=${page}&pageSize=20`)
      .then(r => { if (current) setData(r); })
      .catch(e => { if (current) { setData({ bookings: [], total: 0, page: 1, pageSize: 20 }); onToast(e.message); } });
    return () => { current = false; };
  }, [role, period, page, rev, onToast]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  return (
    <Panel>
      <PH title="내 예약" sub={data ? `${data.total}건` : ''}
        right={<span style={{ display: 'inline-flex', gap: 8 }} data-my-bookings-filter>
          <SegmentedControl size="sm" value={role} onChange={v => { setRole(v); setPage(1); }} options={[{ value: 'requested', label: '예약한 것' }, { value: 'attending', label: '참석하는 것' }]} />
          <SegmentedControl size="sm" value={period} onChange={v => { setPeriod(v); setPage(1); }} options={[{ value: 'upcoming', label: '다가오는' }, { value: 'past', label: '지난·취소' }]} />
        </span>} />
      {!data ? <Loading title="불러오는 중" /> : !data.bookings.length ? <Empty icon="calendar" title="예약이 없습니다" /> : (
        <div data-my-bookings>
          {data.bookings.map(b => <Row key={b.id} b={b} onOpen={onOpen} />)}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 10, alignItems: 'center' }}>
              <button className="ico" aria-label="이전 쪽" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icon name="chevron-left" size={14} /></button>
              <span className="mono" style={{ fontSize: 11.5 }}>{page} / {pages}</span>
              <button className="ico" aria-label="다음 쪽" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><Icon name="chevron-right" size={14} /></button>
            </div>)}
        </div>
      )}
    </Panel>
  );
}

/** 승인 대기 — 내가 승인할 수 있는 예약(반복 예약은 묶음마다 한 줄). 승인은 여기서 바로, 반려는 상세에서 사유와 함께 */
export function PendingApprovals({ list, onOpen, onApprove }) {
  return (
    <Panel>
      <PH title="승인 대기" sub={list ? `${list.length}건 · 담당 설비 예약` : ''} />
      {!list ? <Loading title="불러오는 중" /> : !list.length ? <Empty icon="check-circle" title="승인할 예약이 없습니다" /> : (
        <div data-pending-bookings>
          {list.map(b => {
            const s = b.actions.series;
            return <Row key={b.id} b={b} onOpen={onOpen} right={
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                {s && <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }} data-series-pending={b.series.pending}>반복 {b.series.pending}회</span>}
                {(b.actions.approve || s?.approve) && <Button variant="primary" size="sm" onClick={() => onApprove(b)}>{s ? '반복 전체 승인' : '승인'}</Button>}
                {(b.actions.reject || s?.reject) && <Button variant="secondary" size="sm" onClick={() => onOpen(b.id)}>반려…</Button>}
              </span>} />;
          })}
        </div>
      )}
    </Panel>
  );
}
